// src/components/CourseProgressCard.tsx
// Rule-based progress card showing ALL topics and combined progress

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Clock, Target, ChevronRight } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import { Course, ActivityLog } from '../types';
import {
  getCourseProgress,
  getNextRecommendation,
  formatTimeAgo,
  formatActivityName,
  Recommendation,
} from '../lib/recommendations';

interface CourseProgressCardProps {
  courseId: string;
  course: Course;
  onProgressLoaded?: (progress: ModuleProgress) => void;
  onTopicClick?: (categoryId: string) => void;
}

// Progress data for each module (passed to CourseView for section descriptions)
export interface ModuleProgress {
  lessonsRead: number;
  lessonsTotal: number;
  flashcardsKnown: number;
  flashcardsTotal: number;
  quizBest: number | null;
  quizTotal: number;
  fillInBest: number | null;
  fillInTotal: number;
  transformBest: number | null;
  transformTotal: number;
  vocabMatchDone: boolean;
}

// Progress per topic/category
interface TopicProgress {
  id: string;
  label: string;
  color: string;
  modulesCompleted: number;
  modulesTotal: number;
  quizScore: number | null;
  fillInScore: number | null;
  transformScore: number | null;
  lessonRead: boolean;
  // Detailed module tracking
  hasLesson: boolean;
  hasQuiz: boolean;
  hasFillIn: boolean;
  hasTransform: boolean;
  hasFlashcards: boolean;
  hasVocabMatch: boolean;
  hasVocabQuiz: boolean;
  fillInDone: boolean;
  fillInBestScore: number | null;
  quizCorrect: number;
  quizTotal: number;
  flashcardsCount: number;
  flashcardsKnown: number;
  flashcardsWeaknesses: number;
  vocabMatchCount: number;
  vocabMatchDone: boolean;
  vocabQuizBestScore: number | null;
  hasMathExercises: boolean;
  mathExercisesCount: number;
  mathExercisesDone: boolean;
  mathExercisesBestScore: number | null;
  mathExercisesBestPoints: string | null; // e.g. "4/10"
  hasSongs: boolean;
  songsCount: number;
}

export function CourseProgressCard({ courseId, course, onProgressLoaded, onTopicClick }: CourseProgressCardProps) {
  const { user } = useAuth();
  const [lastActivity, setLastActivity] = useState<ActivityLog | null>(null);
  const [recommendation, setRecommendation] = useState<Recommendation | null>(null);
  const [topicProgress, setTopicProgress] = useState<TopicProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function loadProgress() {
      if (!user?.id) {
        setIsLoading(false);
        return;
      }

      try {
        // 1. Get course progress (includes lastActivity)
        const progress = await getCourseProgress(user.id, courseId, course);
        setLastActivity(progress.lastActivity);

        // 2. Get recommendation
        const rec = getNextRecommendation(courseId, progress, course);
        setRecommendation(rec);

        // 3. Load detailed progress for all topics and modules
        await loadDetailedProgress(user.id, progress);

      } catch (error) {
        console.error('Error loading progress:', error);
      }

      setIsLoading(false);
    }

    async function loadDetailedProgress(userId: string, courseProgress: Awaited<ReturnType<typeof getCourseProgress>>) {
      // Load all data in parallel
      const [quizResults, fillInResults, transformResults, lessonProgress, flashcardProgress, vocabMatchResults, vocabQuizResults, mathExResults] = await Promise.all([
        // Quiz results
        supabase
          .from('quiz_results')
          .select('percentage, wrong_questions, mode, details')
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .order('created_at', { ascending: false }),

        // Fill-in results (from activity_log)
        supabase
          .from('activity_log')
          .select('details')
          .eq('user_id', userId)
          .eq('action', 'fill_in')
          .order('created_at', { ascending: false }),

        // Transform results (from activity_log)
        supabase
          .from('activity_log')
          .select('details')
          .eq('user_id', userId)
          .eq('action', 'transform_quiz')
          .order('created_at', { ascending: false }),

        // Lesson progress
        supabase
          .from('lesson_progress')
          .select('lesson_id, completed')
          .eq('user_id', userId)
          .eq('course_id', courseId),

        // Flashcard progress
        supabase
          .from('flashcard_progress')
          .select('flashcard_id, knew')
          .eq('user_id', userId)
          .eq('course_id', courseId),

        // Vocab match results (from activity_log)
        supabase
          .from('activity_log')
          .select('details')
          .eq('user_id', userId)
          .eq('action', 'vocab_match')
          .order('created_at', { ascending: false }),

        // Vocab quiz results (from quiz_results)
        supabase
          .from('quiz_results')
          .select('percentage, details')
          .eq('user_id', userId)
          .eq('course_id', courseId)
          .eq('mode', 'vocab-quiz')
          .order('created_at', { ascending: false }),

        // Math exercise results (from activity_log)
        supabase
          .from('activity_log')
          .select('details')
          .eq('user_id', userId)
          .eq('action', 'math_exercise')
          .order('created_at', { ascending: false }),
      ]);

      // Calculate module progress for CourseView sections
      const moduleProgress: ModuleProgress = {
        lessonsRead: lessonProgress.data?.filter(l => l.completed).length || 0,
        lessonsTotal: (course.textLessons?.length || 0) + (course.lessons?.length || 0),
        flashcardsKnown: flashcardProgress.data?.filter(f => f.knew && !f.flashcard_id.startsWith('vocab_')).length || 0,
        flashcardsTotal: course.flashcards?.length || 0,
        quizBest: null,
        quizTotal: course.quizQuestions?.length || 0,
        fillInBest: null,
        fillInTotal: course.fillInQuestions?.length || 0,
        transformBest: null,
        transformTotal: course.transformQuestions?.length || 0,
        vocabMatchDone: false,
      };

      // Get best quiz score
      const quizOnly = quizResults.data?.filter(r => r.mode === 'quiz') || [];
      if (quizOnly.length > 0) {
        moduleProgress.quizBest = Math.max(...quizOnly.map(r => r.percentage));
      }

      // Get best fill-in score
      if (fillInResults.data && fillInResults.data.length > 0) {
        const scores = fillInResults.data.map(r => {
          const d = r.details as { percentage?: number };
          return d.percentage || 0;
        });
        moduleProgress.fillInBest = Math.max(...scores);
      }

      // Get best transform score
      if (transformResults.data && transformResults.data.length > 0) {
        const scores = transformResults.data.map(r => {
          const d = r.details as { percentage?: number };
          return d.percentage || 0;
        });
        moduleProgress.transformBest = Math.max(...scores);
      }

      // Check vocab match
      const vocabProgress = flashcardProgress.data?.filter(f => f.flashcard_id.startsWith('vocab_')) || [];
      moduleProgress.vocabMatchDone = vocabProgress.length > 0;

      // Notify parent (CourseView) of progress data
      if (onProgressLoaded) {
        onProgressLoaded(moduleProgress);
      }

      // Build per-topic progress
      const topics: TopicProgress[] = [];

      if (course.categories) {
        // Get questions per category
        const questionsPerCategory: Record<string, { quiz: number; fillIn: number; transform: number }> = {};
        course.categories.forEach(cat => {
          questionsPerCategory[cat.id] = { quiz: 0, fillIn: 0, transform: 0 };
        });

        course.quizQuestions?.forEach(q => {
          if (questionsPerCategory[q.category]) {
            questionsPerCategory[q.category].quiz++;
          }
        });

        course.fillInQuestions?.forEach(q => {
          if (questionsPerCategory[q.category]) {
            questionsPerCategory[q.category].fillIn++;
          }
        });

        course.transformQuestions?.forEach(q => {
          if (questionsPerCategory[q.category]) {
            questionsPerCategory[q.category].transform++;
          }
        });

        // Calculate per-category quiz scores from wrong_questions
        const categoryScores: Record<string, { correct: number; total: number }> = {};
        if (quizOnly.length > 0) {
          const latestQuiz = quizOnly[0];
          const wrongSet = new Set(latestQuiz.wrong_questions || []);

          course.quizQuestions?.forEach(q => {
            if (!categoryScores[q.category]) {
              categoryScores[q.category] = { correct: 0, total: 0 };
            }
            categoryScores[q.category].total++;
            if (!wrongSet.has(q.id)) {
              categoryScores[q.category].correct++;
            }
          });
        }

        // Get lessons per category
        const lessonsPerCategory: Record<string, string[]> = {};
        course.textLessons?.forEach(l => {
          if (!lessonsPerCategory[l.category]) {
            lessonsPerCategory[l.category] = [];
          }
          lessonsPerCategory[l.category].push(l.id);
        });

        const completedLessonIds = new Set(
          lessonProgress.data?.filter(l => l.completed).map(l => l.lesson_id) || []
        );

        // Build per-category flashcard progress by mapping flashcard_id → course.flashcards → category
        const fcProgressByCategory: Record<string, { known: number; total: number; weaknesses: number }> = {};
        if (course.flashcards) {
          // Build flashcard ID → category map
          const fcCategoryMap: Record<string, string> = {};
          course.flashcards.forEach(f => { fcCategoryMap[f.id] = f.category; });

          // Count per category from progress data
          const practicedByCategory: Record<string, { knew: number; didnt: number }> = {};
          flashcardProgress.data?.forEach(fp => {
            if (fp.flashcard_id.startsWith('vocab_')) return; // Skip vocab-match entries
            const cat = fcCategoryMap[fp.flashcard_id];
            if (!cat) return;
            if (!practicedByCategory[cat]) practicedByCategory[cat] = { knew: 0, didnt: 0 };
            if (fp.knew) practicedByCategory[cat].knew++;
            else practicedByCategory[cat].didnt++;
          });

          // Build final per-category stats
          course.categories?.forEach(cat => {
            const totalInCat = course.flashcards.filter(f => f.category === cat.id).length;
            const practiced = practicedByCategory[cat.id] || { knew: 0, didnt: 0 };
            fcProgressByCategory[cat.id] = {
              known: practiced.knew,
              total: totalInCat,
              weaknesses: practiced.didnt,
            };
          });
        }

        // Build per-category vocab-match completion by mapping set_id → course.vocabSets → category
        const vmDoneByCategory: Record<string, boolean> = {};
        if (course.vocabSets && vocabMatchResults.data) {
          // Build set_id → category map
          const setCategoryMap: Record<string, string> = {};
          course.vocabSets.forEach(vs => { setCategoryMap[vs.id] = vs.category; });

          vocabMatchResults.data.forEach(r => {
            const d = r.details as { set_id?: string; course_id?: string };
            if (d.course_id !== courseId) return;
            const cat = d.set_id ? setCategoryMap[d.set_id] : undefined;
            if (cat) vmDoneByCategory[cat] = true;
          });
        }

        // Build per-category fill-in best score
        const fillInBestByCategory: Record<string, number> = {};
        fillInResults.data?.forEach(r => {
          const d = r.details as { category?: string; percentage?: number; course_id?: string };
          if (d.course_id !== courseId) return;
          if (d.category && d.percentage !== undefined) {
            if (!fillInBestByCategory[d.category] || d.percentage > fillInBestByCategory[d.category]) {
              fillInBestByCategory[d.category] = d.percentage;
            }
          }
        });

        // Build per-category math exercise completion
        const mathDoneByCategory: Record<string, boolean> = {};
        const mathBestByCategory: Record<string, number> = {};
        const mathBestPointsByCategory: Record<string, string> = {};
        mathExResults.data?.forEach(r => {
          const d = r.details as { category?: string; course_id?: string; percentage?: number; score?: number; total?: number };
          if (d.course_id !== courseId) return;
          if (d.category) {
            mathDoneByCategory[d.category] = true;
            if (d.percentage !== undefined) {
              if (!mathBestByCategory[d.category] || d.percentage > mathBestByCategory[d.category]) {
                mathBestByCategory[d.category] = d.percentage;
                if (d.score !== undefined && d.total !== undefined) {
                  mathBestPointsByCategory[d.category] = `${d.score}/${d.total}`;
                }
              }
            }
          }
        });

        // Categories to exclude from display
        const excludedCategories = new Set<string>();

        // Note: Deutsch course categories now directly match lesson topics,
        // so no exclusions needed

        // Build topic list
        for (const cat of course.categories) {
          // Skip excluded categories
          if (excludedCategories.has(cat.id)) continue;

          const qpc = questionsPerCategory[cat.id] || { quiz: 0, fillIn: 0, transform: 0 };
          const catLessons = lessonsPerCategory[cat.id] || [];

          // Skip categories with no content at all
          const catSongsCount = course.songs?.filter(s => s.category === cat.id).length || 0;
          if (qpc.quiz === 0 && qpc.fillIn === 0 && qpc.transform === 0 && catLessons.length === 0 && catSongsCount === 0) {
            continue;
          }

          // Count modules for this topic
          let modulesTotal = 0;
          let modulesCompleted = 0;

          const hasLesson = catLessons.length > 0;
          const hasQuiz = qpc.quiz > 0;
          const hasFillIn = qpc.fillIn > 0;
          const hasTransform = qpc.transform > 0 || cat.id === 'indirect-speech';
          const isDeutsch = courseId === 'deutsch-schularbeit';

          // Lesson module (catLessons already defined above)
          if (hasLesson) {
            modulesTotal++;
            const readCount = catLessons.filter(lid => completedLessonIds.has(lid)).length;
            if (readCount === catLessons.length) modulesCompleted++;
          }

          // Fill-in module (linked to lesson for Deutsch)
          let fillInDoneForCat = false;
          if (hasFillIn) {
            modulesTotal++;
            fillInDoneForCat = fillInResults.data?.some(r => {
              const d = r.details as { category?: string };
              return d.category === cat.id;
            }) || false;
            if (fillInDoneForCat) modulesCompleted++;
          }

          // Quiz module – count for all courses except deutsch-schularbeit
          // (Deutsch Quiz is a separate global module, not per-topic)
          if (hasQuiz && courseId !== 'deutsch-schularbeit') {
            modulesTotal++;
            const score = categoryScores[cat.id];
            if (score && score.total > 0 && (score.correct / score.total) >= 0.7) {
              modulesCompleted++;
            }
          }

          // Transform module (only for indirect-speech category, non-Deutsch)
          if (hasTransform && courseId !== 'deutsch-schularbeit') {
            modulesTotal++;
            if (moduleProgress.transformBest !== null && moduleProgress.transformBest >= 70) {
              modulesCompleted++;
            }
          }

          // Calculate scores
          let quizScore: number | null = null;
          if (categoryScores[cat.id] && categoryScores[cat.id].total > 0) {
            quizScore = Math.round((categoryScores[cat.id].correct / categoryScores[cat.id].total) * 100);
          }

          const catScore = categoryScores[cat.id];

          // Count flashcards and vocab sets for this category
          const fcCount = course.flashcards?.filter(f => f.category === cat.id).length || 0;
          const vsCount = course.vocabSets?.filter(v => v.category === cat.id).length || 0;

          // Per-category flashcard stats
          const fcStats = fcProgressByCategory[cat.id] || { known: 0, total: 0, weaknesses: 0 };

          // Per-category vocab-quiz best score (map from quiz_results via VocabQuiz category param)
          // VocabQuiz is global — not per category, so we leave it as null for now
          const vqBestScore: number | null = null;

          // Songs module
          if (catSongsCount > 0) {
            modulesTotal++;
            // Songs count as "completed" just by existing (listening is optional bonus)
            modulesCompleted++;
          }

          topics.push({
            id: cat.id,
            label: cat.label,
            color: cat.color,
            modulesCompleted,
            modulesTotal: modulesTotal || 1,
            quizScore,
            fillInScore: fillInBestByCategory[cat.id] ?? null,
            transformScore: cat.id === 'indirect-speech' ? moduleProgress.transformBest : null,
            lessonRead: catLessons.length > 0 && catLessons.every(lid => completedLessonIds.has(lid)),
            hasLesson,
            hasQuiz,
            hasFillIn,
            hasTransform,
            hasFlashcards: fcCount > 0,
            hasVocabMatch: vsCount > 0,
            hasVocabQuiz: fcCount >= 4,
            fillInDone: fillInDoneForCat,
            fillInBestScore: fillInBestByCategory[cat.id] ?? null,
            quizCorrect: catScore?.correct ?? 0,
            quizTotal: catScore?.total ?? 0,
            flashcardsCount: fcCount,
            flashcardsKnown: fcStats.known,
            flashcardsWeaknesses: fcStats.weaknesses,
            vocabMatchCount: vsCount,
            vocabMatchDone: vmDoneByCategory[cat.id] || false,
            vocabQuizBestScore: vqBestScore,
            hasMathExercises: (course.mathExercises?.filter(m => m.category === cat.id).length || 0) > 0,
            mathExercisesCount: course.mathExercises?.filter(m => m.category === cat.id).length || 0,
            mathExercisesDone: mathDoneByCategory[cat.id] || false,
            mathExercisesBestScore: mathBestByCategory[cat.id] ?? null,
            mathExercisesBestPoints: mathBestPointsByCategory[cat.id] ?? null,
            hasSongs: catSongsCount > 0,
            songsCount: catSongsCount,
          });
        }
      }

      // Sort: incomplete first, then by score
      topics.sort((a, b) => {
        // First sort by completion (incomplete first)
        if (a.modulesCompleted < a.modulesTotal && b.modulesCompleted >= b.modulesTotal) return -1;
        if (a.modulesCompleted >= a.modulesTotal && b.modulesCompleted < b.modulesTotal) return 1;

        // Then by quiz score (low scores first)
        const scoreA = a.quizScore ?? 999;
        const scoreB = b.quizScore ?? 999;
        return scoreA - scoreB;
      });

      setTopicProgress(topics);
    }

    loadProgress();
  }, [user?.id, courseId, course, onProgressLoaded]);

  if (isLoading) {
    return (
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '20px',
        textAlign: 'center',
      }}>
        <div className="loading" style={{ minHeight: '100px', padding: '20px' }}>Laden...</div>
      </div>
    );
  }

  // Format last activity details
  const getLastActivityDetail = () => {
    if (!lastActivity) return null;
    const details = lastActivity.details as Record<string, unknown>;

    if (details.percentage !== undefined) {
      return `${details.percentage}%`;
    }
    if (details.cards_known !== undefined && details.cards_reviewed !== undefined) {
      return `${details.cards_known}/${details.cards_reviewed} gewusst`;
    }
    if (details.pairs_matched !== undefined) {
      return `${details.pairs_matched} Paare`;
    }
    if (details.set_title) {
      return String(details.set_title);
    }
    return null;
  };

  // Get status icon for topic
  const getTopicIcon = (topic: TopicProgress) => {
    if (topic.modulesCompleted === 0) return '❌';
    if (topic.modulesCompleted >= topic.modulesTotal) return '✅';
    if (topic.quizScore !== null && topic.quizScore < 70) return '⚠️';
    return '🔄';
  };

  // Module status: not_started (❌), in_progress (🔄), completed (✅)
  type ModuleStatus = 'not_started' | 'in_progress' | 'completed';

  // Build module tag list for a topic — shows all available modules with status + score
  const getTopicModuleTags = (topic: TopicProgress): { label: string; status: ModuleStatus; path: string; score: string }[] => {
    const tags: { label: string; status: ModuleStatus; path: string; score: string }[] = [];
    const cat = topic.id;

    // 📖 Lektion: completed if read, otherwise not_started
    if (topic.hasLesson) {
      tags.push({
        label: '📖 Lernstoff',
        status: topic.lessonRead ? 'completed' : 'not_started',
        path: `/course/${courseId}/text-lessons?category=${cat}`,
        score: topic.lessonRead ? '✓' : '—',
      });
    }

    // ✏️ Lückentext: RIGHT AFTER Lernstoff (it's the linked exercise)
    if (topic.hasFillIn) {
      let status: ModuleStatus = 'not_started';
      if (topic.fillInBestScore !== null && topic.fillInBestScore >= 90) status = 'completed';
      else if (topic.fillInDone) status = 'in_progress';
      tags.push({
        label: '✏️ Lückentext',
        status,
        path: `/course/${courseId}/fill-in?category=${cat}`,
        score: topic.fillInBestScore !== null ? `${topic.fillInBestScore}%` : '—',
      });
    }

    // 🃏 Lernkarten
    if (topic.hasFlashcards) {
      const practiced = topic.flashcardsKnown + topic.flashcardsWeaknesses;
      let status: ModuleStatus = 'not_started';
      if (practiced > 0 && topic.flashcardsWeaknesses === 0) status = 'completed';
      else if (practiced > 0) status = 'in_progress';
      const pct = topic.flashcardsCount > 0 ? Math.round((topic.flashcardsKnown / topic.flashcardsCount) * 100) : 0;
      tags.push({
        label: `🃏 ${topic.flashcardsCount} Karten`,
        status,
        path: `/course/${courseId}/flashcards?category=${cat}`,
        score: practiced > 0 ? `${topic.flashcardsKnown}/${topic.flashcardsCount}` : '—',
      });
    }

    // 🔀 Matching
    if (topic.hasVocabMatch) {
      tags.push({
        label: '🔀 Matching',
        status: topic.vocabMatchDone ? 'completed' : 'not_started',
        path: `/course/${courseId}/vocab-match?category=${cat}`,
        score: topic.vocabMatchDone ? '✓' : '—',
      });
    }

    // 🎯 Karten-Quiz
    if (topic.hasVocabQuiz) {
      let status: ModuleStatus = 'not_started';
      if (topic.vocabQuizBestScore !== null && topic.vocabQuizBestScore >= 90) status = 'completed';
      else if (topic.vocabQuizBestScore !== null) status = 'in_progress';
      tags.push({
        label: '🎯 Karten-Quiz',
        status,
        path: `/course/${courseId}/vocab-quiz?category=${cat}`,
        score: topic.vocabQuizBestScore !== null ? `${topic.vocabQuizBestScore}%` : '—',
      });
    }

    // 📝 Quiz
    if (topic.hasQuiz) {
      let status: ModuleStatus = 'not_started';
      if (topic.quizScore !== null && topic.quizScore >= 90) status = 'completed';
      else if (topic.quizScore !== null) status = 'in_progress';
      tags.push({
        label: '📝 Quiz',
        status,
        path: `/course/${courseId}/quiz?category=${cat}`,
        score: topic.quizScore !== null ? `${topic.quizScore}%` : '—',
      });
    }

    // 🔄 Umwandlung
    if (topic.hasTransform) {
      let status: ModuleStatus = 'not_started';
      if (topic.transformScore !== null && topic.transformScore >= 90) status = 'completed';
      else if (topic.transformScore !== null) status = 'in_progress';
      tags.push({
        label: '🔄 Umwandlung',
        status,
        path: `/course/${courseId}/transform?category=${cat}`,
        score: topic.transformScore !== null ? `${topic.transformScore}%` : '—',
      });
    }

    // 🧮 Rechenaufgaben
    if (topic.hasMathExercises) {
      tags.push({
        label: `🧮 ${topic.mathExercisesCount} Aufgaben`,
        status: topic.mathExercisesDone ? 'completed' : 'not_started',
        path: `/course/${courseId}/math-exercise?category=${cat}`,
        score: topic.mathExercisesBestPoints || (topic.mathExercisesDone ? '✓' : '—'),
      });
    }

    // 🎵 Songs
    if (topic.hasSongs) {
      tags.push({
        label: `🎵 ${topic.songsCount} ${topic.songsCount === 1 ? 'Song' : 'Songs'}`,
        status: 'completed',
        path: `/course/${courseId}/songs`,
        score: '♪',
      });
    }

    return tags;
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      {/* Last Activity */}
      {lastActivity && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '12px',
          padding: '12px 16px',
          marginBottom: '12px',
        }}>
          <Clock size={18} style={{ color: 'rgba(255,255,255,0.5)' }} />
          <div style={{ flex: 1 }}>
            <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              Zuletzt: {formatActivityName(lastActivity.action)}
            </span>
            {getLastActivityDetail() && (
              <span style={{ color: '#ffd54f', fontSize: '14px', marginLeft: '8px' }}>
                – {getLastActivityDetail()}
              </span>
            )}
            <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '13px', marginLeft: '8px' }}>
              ({formatTimeAgo(lastActivity.created_at)})
            </span>
          </div>
        </div>
      )}

      {/* Next Recommendation */}
      {recommendation && (
        <Link
          to={recommendation.route}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'linear-gradient(135deg, rgba(255, 213, 79, 0.15), rgba(255, 152, 0, 0.1))',
            border: '1px solid rgba(255, 213, 79, 0.3)',
            borderRadius: '12px',
            padding: '14px 16px',
            marginBottom: '12px',
            textDecoration: 'none',
          }}
        >
          <Target size={22} style={{ color: '#ffd54f' }} />
          <div style={{ flex: 1 }}>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginBottom: '2px' }}>
              Naechster Schritt
            </div>
            <div style={{ color: '#ffd54f', fontSize: '15px', fontWeight: 600 }}>
              {recommendation.emoji} {recommendation.text}
            </div>
          </div>
          <ChevronRight size={20} style={{ color: '#ffd54f' }} />
        </Link>
      )}

      {/* Topic Progress - ALL topics */}
      {topicProgress.length > 0 && (
        <div style={{
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px',
          padding: '14px 16px',
          marginBottom: '12px',
        }}>
          <div style={{
            color: 'rgba(255,255,255,0.6)',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '12px',
          }}>
            Fortschritt nach Thema
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {topicProgress.map(topic => (
              <div
                key={topic.id}
                onClick={onTopicClick ? () => onTopicClick(topic.id) : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  fontSize: '14px',
                  cursor: onTopicClick ? 'pointer' : 'default',
                  padding: onTopicClick ? '10px 12px' : undefined,
                  borderRadius: onTopicClick ? '10px' : undefined,
                  background: onTopicClick ? 'rgba(255, 255, 255, 0.03)' : undefined,
                  border: onTopicClick ? '1px solid rgba(255, 255, 255, 0.08)' : 'none',
                  transition: 'all 0.2s ease',
                }}
              >
                <span style={{ width: '20px', textAlign: 'center', flexShrink: 0 }}>
                  {getTopicIcon(topic)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    color: topic.modulesCompleted === 0 ? 'rgba(255,255,255,0.4)' : '#e8eaf6',
                    fontSize: '14px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                    {topic.label}
                  </div>
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px',
                    marginTop: '4px',
                  }}>
                    {getTopicModuleTags(topic).map((tag, i) => {
                      const statusIcon = tag.status === 'completed' ? '✅' : tag.status === 'in_progress' ? '🔄' : '❌';
                      const statusColor = tag.status === 'completed' ? '#4caf50' : tag.status === 'in_progress' ? '#ff9800' : 'rgba(255,255,255,0.4)';
                      const statusBg = tag.status === 'completed' ? 'rgba(76, 175, 80, 0.15)' : tag.status === 'in_progress' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 255, 255, 0.04)';
                      const statusBorder = tag.status === 'completed' ? 'rgba(76, 175, 80, 0.3)' : tag.status === 'in_progress' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255,255,255,0.08)';
                      const scoreColor = tag.status === 'completed' ? '#4caf50' : tag.status === 'in_progress' ? '#ffd54f' : 'rgba(255,255,255,0.25)';
                      return (
                        <Link
                          key={i}
                          to={tag.path}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            fontSize: '11px',
                            padding: '2px 6px 3px',
                            borderRadius: '6px',
                            background: statusBg,
                            color: statusColor,
                            border: `1px solid ${statusBorder}`,
                            whiteSpace: 'nowrap',
                            textDecoration: 'none',
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '0px',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                            <span style={{ fontSize: '9px' }}>{statusIcon}</span>
                            {tag.label}
                          </span>
                          <span style={{ fontSize: '9px', color: scoreColor, lineHeight: 1 }}>
                            {tag.score}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                </div>
                {onTopicClick && (
                  <ChevronRight size={18} style={{ color: 'rgba(255,255,255,0.3)', flexShrink: 0, marginTop: '2px' }} />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Link to full AI Lernstand */}
      <Link
        to={`/lernstand/${courseId}`}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          background: 'rgba(171, 71, 188, 0.15)',
          border: '1px solid rgba(171, 71, 188, 0.3)',
          borderRadius: '10px',
          padding: '12px',
          color: '#ab47bc',
          fontSize: '14px',
          fontWeight: 600,
          textDecoration: 'none',
        }}
      >
        🤖 Detaillierte KI-Analyse
      </Link>
    </div>
  );
}
